import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity } from '@common/types';
import { IBuildingTableFilters } from '../types';

export type PersistedBuildingColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialBuildingColumns: PersistedBuildingColumn[] = [
  {
    field: 'name',
    headerName: 'Building',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'operator_name',
    headerName: 'Operator',
    minWidth: 160,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'wellness_coordinators',
    headerName: 'Wellness Coordinator',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'address',
    headerName: 'Address',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'total_beds',
    headerName: 'Total Beds',
    width: 120,
    minWidth: 120,
    sortable: false,
    filterable: false,
  },
];

export const initialBuildingPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

type TBuildingTableStore = {
  isFiltersCardOpen: boolean;
  handleSetIsFiltersCardOpen: (isOpen: boolean) => void;
  handleToggleFiltersCard: () => void;

  filters: IBuildingTableFilters;
  draftFilters: IBuildingTableFilters;

  setFilters: (newFilters: Partial<IBuildingTableFilters>) => void;
  setDraftFilters: (newFilters: Partial<IBuildingTableFilters>) => void;

  applyDraftFilters: () => void;
  resetFilters: () => void;
  resetDraftFilters: () => void;
  syncDraftWithAppliedFilters: () => void;
  clearStore: () => void;

  appliedFiltersCount: number;
  draftFiltersCount: number;

  columns: PersistedBuildingColumn[];
  setColumns: (columns: PersistedBuildingColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

const getDefaultFilters = (): IBuildingTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 100,
  partner_id: '',
  operator_id: '',
  state: '',
  county: '',
});

type TKeyOfFilters = keyof IBuildingTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IBuildingTableFilters,
  appliedFilters: IBuildingTableFilters,
): number => {
  let count = 0;

  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  filtersKeyList.forEach(key => {
    if (!isEqual(initialFiltersList[key], appliedFilters[key])) {
      count += 1;
    }
  });

  return count;
};

export const useBuildingTableStore = create<TBuildingTableStore>()(
  persist(
    (set, _) => ({
      isFiltersCardOpen: false,

      handleSetIsFiltersCardOpen: isOpen =>
        set(() => ({
          isFiltersCardOpen: isOpen,
        })),

      handleToggleFiltersCard: () =>
        set(state => ({
          isFiltersCardOpen: !state.isFiltersCardOpen,
        })),

      filters: getDefaultFilters(),
      draftFilters: getDefaultFilters(),

      setFilters: newFilters =>
        set(state => {
          const updatedFilters = {
            ...state.filters,
            ...newFilters,
          };

          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),

      setDraftFilters: newFilters =>
        set(state => {
          const updatedDraftFilters = {
            ...state.draftFilters,
            ...newFilters,
          };

          return {
            draftFilters: updatedDraftFilters,
            draftFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedDraftFilters,
            ),
          };
        }),

      applyDraftFilters: () =>
        set(state => {
          const appliedFilters = {
            ...state.draftFilters,
            page: 1,
          };

          return {
            filters: appliedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              appliedFilters,
            ),
          };
        }),

      resetFilters: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          filters: defaults,
          draftFilters: defaults,
          appliedFiltersCount: 0,
          draftFiltersCount: 0,
        }));
      },

      resetDraftFilters: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          draftFilters: defaults,
          draftFiltersCount: 0,
        }));
      },

      syncDraftWithAppliedFilters: () =>
        set(state => {
          const preservedFilters = {
            ...getDefaultFilters(),
            sort_by: state.filters.sort_by,
            sort_order: state.filters.sort_order,
          };

          return {
            filters: preservedFilters,
            draftFilters: preservedFilters,
            appliedFiltersCount: 0,
            draftFiltersCount: 0,
            isFiltersCardOpen: false,
          };
        }),

      clearStore: () => {
        const defaults = getDefaultFilters();

        return set(() => ({
          filters: defaults,
          draftFilters: defaults,
          appliedFiltersCount: 0,
          draftFiltersCount: 0,
          isFiltersCardOpen: false,
        }));
      },

      appliedFiltersCount: 0,
      draftFiltersCount: 0,

      columns: initialBuildingColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialBuildingPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialBuildingColumns,
          pinnedColumns: initialBuildingPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'building-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TBuildingTableStore &
            Pick<IBuildingTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IBuildingTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
          draftFilters: mergedFilters,
        };
      },
    },
  ),
);
